import axios from "axios";
import React, { useEffect, useState } from "react";
import { Popconfirm } from "antd";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL } from "../../utils/consts";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "../../utils/useAuth";
import { isAdmin, isModerator, isSuperAdmin } from "../../utils/permissions";
import AddUserModal from "../../Components/Modals/AddUserModal/AddUserModal";
import { setWorkingPatterns } from "../../utils/redux/slices/workingPatternSlice";
import { usePermissions } from "../../utils/usePermissions";
import {
  useGetUsersQuery,
  useGetWorkSectorsQuery,
  useGetWorkingPatternsQuery,
} from "../../utils/redux/api/orgDataApi";
import "./Staff.scss";

type ViewMode = "table" | "compact" | "kanban";

const PAGE_SIZE = 10;

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f97316, #ec4899)",
  "linear-gradient(135deg, #3b82f6, #8b5cf6)",
  "linear-gradient(135deg, #10b981, #3b82f6)",
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #8b5cf6, #ec4899)",
  "linear-gradient(135deg, #06b6d4, #3b82f6)",
  "linear-gradient(135deg, #22c55e, #10b981)",
  "linear-gradient(135deg, #ef4444, #f97316)",
];

const getAvatarGradient = (id: string, frozen?: boolean) => {
  if (frozen) return "linear-gradient(135deg, #94a3b8, #64748b)";
  if (!id) return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[id.charCodeAt(id.length - 1) % AVATAR_GRADIENTS.length];
};

const getInitials = (firstName = "", lastName = "") =>
  `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();

const Staff: React.FC = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state.userData.user);
  const [isOpenUserModal, setIsOpenUserModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFrozen, setShowFrozen] = useState(false);
  const navigate = useNavigate();
  const [selectedSector, setSelectedSector] = useState("");
  const { t } = useTranslation();
  const authUser: any = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [page, setPage] = useState(1);
  const { hasPermission } = usePermissions();
  const isSuper = isSuperAdmin(userData?.roles || authUser?.roles);
  const canCreate = isAdmin(authUser?.roles) && (hasPermission("staff:create") || isSuper);
  const canDelete = hasPermission("staff:delete") || isSuper;
  const canView =
    isSuperAdmin(authUser?.roles) || isAdmin(authUser?.roles) || isModerator(authUser?.roles);

  const { data: patients, refetch: refetchPatients } = useGetUsersQuery(
    userData?.clientId,
    { skip: !userData?.clientId },
  );
  const { data: workSectors = [] } = useGetWorkSectorsQuery(userData?.clientId, {
    skip: !userData?.clientId,
  });
  const { data: workingPatternsData } = useGetWorkingPatternsQuery(userData?.clientId, {
    skip: !userData?.clientId,
  });

  // Other pages still read working patterns from this Redux slice — keep it
  // populated alongside the cached/deduped RTK Query fetch above.
  useEffect(() => {
    if (workingPatternsData) dispatch(setWorkingPatterns(workingPatternsData));
  }, [workingPatternsData, dispatch]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const filteredPatients = patients?.filter((item: any) => {
    const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector =
      !selectedSector ||
      item.positionInSector?.some(
        (pos: any) =>
          pos.workSector?._id === selectedSector || pos.workSector === selectedSector
      );
    const matchesFrozen = showFrozen ? true : !item?.isFrozen;
    return matchesSearch && matchesSector && matchesFrozen;
  });

  const totalCount = filteredPatients?.length ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const pagedPatients = filteredPatients?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const effectiveView: ViewMode = isMobile ? "compact" : viewMode;

  const activeCount = patients?.filter((e: any) => !e.isFrozen).length ?? 0;
  const frozenCount = patients?.filter((e: any) => e.isFrozen).length ?? 0;

  const handleDeleteUser = (record: any) => {
    axios
      .delete(`${BASE_API_URL}/user/${record.clientId}/${record._id}/delete`)
      .then(() => refetchPatients())
      .catch((err) => console.error("Delete error:", err));
  };

  const onAddUser = (data: any) => {
    axios
      .post(`${BASE_API_URL}/auth/signup`, {
        email: data.email,
        password: data.password,
        clientId: userData.clientId,
        color: data.color,
        roles: [data.role],
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender,
        workingPatternId: data.workingPatternId,
        positionInSector: data.positionInSector,
        contractStart: data.contractStart,
        contractEnd: data.contractEnd || null,
        sanitaryBookletIssuedAt: data.sanitaryBookletIssuedAt || null,
        sanitaryBookletValidUntil: data.sanitaryBookletValidUntil || null,
        location: data.locationId,
        assignedLocationId: data.assignedLocationId || null,
      })
      .then(() => refetchPatients().finally(() => setIsOpenUserModal(false)))
      .catch((err) => console.error(err));
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const renderAvatar = (emp: any, size = 36) => {
    const fontSize = Math.round(size * 0.33);
    return (
      <div
        className="z-avatar"
        style={{
          width: size,
          height: size,
          fontSize,
          background: emp.avatar?.url ? undefined : getAvatarGradient(emp._id, emp.isFrozen),
        }}
      >
        {emp.avatar?.url ? (
          <img
            src={emp.avatar.url}
            alt={emp.firstName}
            style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          getInitials(emp.firstName, emp.lastName)
        )}
        <span className={`dot${emp.isFrozen ? " frozen" : ""}`} />
      </div>
    );
  };

  const renderStatus = (emp: any) => (
    <span className={`z-status${emp.isFrozen ? " frozen" : ""}`}>
      {emp.isFrozen ? t("Frozen") : t("Active")}
    </span>
  );

  const renderPosition = (emp: any) => {
    const pos = emp.positionInSector?.[0];
    if (!pos) return <span className="z-empty">—</span>;
    const color = pos.workSector?.color ?? "#2563eb";
    return (
      <div className="z-pos">
        <span className="z-pos-name">{pos.name}</span>
        <span className="z-pos-sect" style={{ "--sect-color": color } as React.CSSProperties}>
          {pos.workSector?.name ?? ""}
        </span>
      </div>
    );
  };

  const renderActions = (emp: any) => {
    if ((!hasPermission("staff:open-profile") && !isSuper) || !canView) return null;
    return (
      <div className="z-actions">
        <button
          className="z-action-btn primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/users/${emp._id}`);
          }}
        >
          {t("Open Profile")}
          <span className="z-row-arrow">›</span>
        </button>
        {canDelete && (
          <Popconfirm
            title={t("Are you sure you want to delete this item?")}
            onConfirm={() => handleDeleteUser(emp)}
            okText={t("Yes")}
            cancelText={t("No")}
          >
            <button className="z-action-btn danger" onClick={(e) => e.stopPropagation()}>
              {t("Delete")}
            </button>
          </Popconfirm>
        )}
      </div>
    );
  };

  // ─── Table view ──────────────────────────────────────────────────────────────
  const renderTable = () => (
    <>
      <div className="z-table-wrap">
        <table className="z-table">
          <thead>
            <tr>
              <th style={{ width: 52 }}></th>
              <th>{t("Employee")}</th>
              <th>{t("Email")}</th>
              <th>{t("Position in Sector")}</th>
              <th>{t("Phone Number")}</th>
              <th>{t("Status")}</th>
              <th style={{ textAlign: "right" }}>{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {pagedPatients?.length ? (
              pagedPatients.map((emp: any) => (
                <tr
                  key={emp._id}
                  onClick={() => canView && navigate(`/users/${emp._id}`)}
                >
                  <td>{renderAvatar(emp)}</td>
                  <td>
                    <div className="z-emp">
                      <div>
                        <div className="z-name">
                          {emp.firstName} {emp.lastName}
                        </div>
                        <div className="z-handle">{emp.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {emp.email ? (
                      <div className="z-mail">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="M3 7l9 6 9-6" />
                        </svg>
                        {emp.email}
                        <button
                          className="copy-mail"
                          title={t("Copy")}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard?.writeText(emp.email);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                            <rect x="9" y="9" width="11" height="11" rx="2" />
                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <span className="z-empty">—</span>
                    )}
                  </td>
                  <td>{renderPosition(emp)}</td>
                  <td>
                    <span className={`z-phone${emp.phoneNumber ? "" : " empty"}`}>
                      {emp.phoneNumber || "—"}
                    </span>
                  </td>
                  <td>{renderStatus(emp)}</td>
                  <td>{renderActions(emp)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}>
                  {patients === undefined ? t("Loading...") : t("No employees found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="z-foot">
          <div>
            {t("Showing")}{" "}
            <strong>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)}
            </strong>{" "}
            {t("of")} <strong>{totalCount}</strong>
          </div>
          <div className="z-pager">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => i + 1).map((p) => (
              <button key={p} className={p === page ? "active" : ""} onClick={() => setPage(p)}>
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );

  // ─── Compact view ─────────────────────────────────────────────────────────────
  const renderCompact = () => (
    <div className="z-compact-wrap">
      {filteredPatients?.length ? (
        filteredPatients.map((emp: any) => {
          const pos = emp.positionInSector?.[0];
          const color = pos?.workSector?.color ?? "#2563eb";
          return (
            <div
              key={emp._id}
              className="z-compact-row"
              onClick={() => canView && navigate(`/users/${emp._id}`)}
            >
              <div className="z-emp">
                {renderAvatar(emp, 28)}
                <div>
                  <span className="z-name">
                    {emp.firstName} {emp.lastName}
                  </span>
                  {pos?.name && (
                    <span className="z-compact-position">{pos.name}</span>
                  )}
                </div>
              </div>
              <div className="z-compact-meta">
                {pos?.workSector?.name && (
                  <span
                    className="sect-tag"
                    style={{ "--sect-color": color } as React.CSSProperties}
                  >
                    {pos.workSector.name}
                  </span>
                )}
                <span className="z-phone">{emp.phoneNumber || "—"}</span>
              </div>
              {renderStatus(emp)}
            </div>
          );
        })
      ) : (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}>
          {patients === undefined ? t("Loading...") : t("No employees found")}
        </div>
      )}
    </div>
  );

  // ─── Kanban view ──────────────────────────────────────────────────────────────
  const renderKanban = () => {
    const groups = workSectors
      .map((sector) => ({
        sector,
        employees:
          filteredPatients?.filter((emp: any) =>
            emp.positionInSector?.some(
              (pos: any) => pos.workSector?._id === sector._id
            )
          ) ?? [],
      }))
      .filter((g) => g.employees.length > 0);

    const unassigned =
      filteredPatients?.filter((emp: any) => !emp.positionInSector?.length) ?? [];

    const allGroups = [
      ...groups,
      ...(unassigned.length
        ? [{ sector: { _id: "__none", name: t("Unassigned"), color: "#94a3b8" }, employees: unassigned }]
        : []),
    ];

    if (!allGroups.length) {
      return (
        <div className="z-empty-state">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p>{patients === undefined ? t("Loading...") : t("No employees found")}</p>
        </div>
      );
    }

    return (
      <div className="z-kanban">
        {allGroups.map(({ sector, employees }) => (
          <div key={sector._id} className="z-col">
            <div className="z-col-head">
              <span
                className="z-col-dot"
                style={{ "--sect-color": sector.color } as React.CSSProperties}
              />
              <span className="z-col-title">{sector.name}</span>
              <span className="z-col-count">{employees.length}</span>
            </div>
            {employees.map((emp: any) => (
              <div
                key={emp._id}
                className="z-card"
                onClick={() => canView && navigate(`/users/${emp._id}`)}
              >
                {renderAvatar(emp, 30)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="z-name">
                    {emp.firstName} {emp.lastName}
                  </div>
                  <div className="z-card-pos">
                    {emp.positionInSector?.[0]?.name ?? "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="mainContainer staff-page">
      {/* Page header */}
      <div className="z-head">
        <div>
          <h1 className="z-title">
            {t("Employees")}
            <span className="z-count">{patients?.length ?? 0}</span>
          </h1>
          <div className="z-title-sub">
            {t("Overview of all employees")} · {activeCount} {t("active")},{" "}
            {frozenCount} {t("frozen")}
          </div>
        </div>
        <div className="z-spacer" />
        {canCreate && (
          <>
            <button
              className="btn ghost"
              onClick={() => navigate("/hierarchy")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="3" width="6" height="5" rx="1" />
                <rect x="3" y="16" width="6" height="5" rx="1" />
                <rect x="15" y="16" width="6" height="5" rx="1" />
                <path d="M12 8v4M6 16v-2h12v2" />
              </svg>
              {t("Organization Chart")}
            </button>
            <button className="z-cta" onClick={() => setIsOpenUserModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("Add Employee")}
            </button>
          </>
        )}
      </div>

      {/* Toolbar */}
      <div className="z-toolbar">
        <div className="z-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.5-4.5" />
          </svg>
          <input
            placeholder={t("Search by name, email or phone...")}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
          {searchTerm && (
            <button className="z-search-clear" onClick={() => setSearchTerm("")}>
              ×
            </button>
          )}
        </div>

        {workSectors.length > 0 && (
          <select
            className="z-sector-select"
            value={selectedSector}
            onChange={(e) => { setSelectedSector(e.target.value); setPage(1); }}
          >
            <option value="">{t("All sectors")}</option>
            {workSectors.map((s) => (
              <option key={s._id} value={s._id}>
                {s.name}
                {s.location?.name ? ` – ${s.location.name}` : ""}
              </option>
            ))}
          </select>
        )}

        {!isMobile && (
          <div className="z-views">
            <button
              className={viewMode === "table" ? "active" : ""}
              onClick={() => setViewMode("table")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M3 15h18" />
              </svg>
              {t("Table")}
            </button>
            <button
              className={viewMode === "compact" ? "active" : ""}
              onClick={() => setViewMode("compact")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {t("Compact")}
            </button>
            <button
              className={viewMode === "kanban" ? "active" : ""}
              onClick={() => setViewMode("kanban")}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                <rect x="3" y="3" width="6" height="18" rx="1.5" />
                <rect x="11" y="3" width="6" height="12" rx="1.5" />
              </svg>
              {t("Kanban")}
            </button>
          </div>
        )}

        <label className="z-frozen">
          <input
            type="checkbox"
            checked={showFrozen}
            onChange={(e) => setShowFrozen(e.target.checked)}
          />
          <span className="z-switch" />
          <span>{t("Show frozen")}</span>
        </label>
      </div>

      <AddUserModal
        isOpen={isOpenUserModal}
        onCloseModal={() => setIsOpenUserModal(false)}
        onAddUser={onAddUser}
        userData={userData}
      />

      {effectiveView === "table" && renderTable()}
      {effectiveView === "compact" && renderCompact()}
      {effectiveView === "kanban" && renderKanban()}
    </div>
  );
};

export default Staff;
